import { useAutoneTranslation } from '@autone/translations';
import {
  CardToggleGroup,
  cn,
  TailwindFormField,
  type ToggleGroupItemProps,
  type ToggleGroupSingleProps,
} from '@autone/ui';
import { type Control, type FieldValues, type Path } from 'react-hook-form';

import { type APP_NAME } from '../../../consts';

import { type AggregationType } from './aggregation-type-form-schema';

interface AggregationTypeRadioToggleGroupProps<TFieldValues extends FieldValues>
  extends Omit<ToggleGroupSingleProps, 'type' | 'defaultValue'> {
  control: Control<TFieldValues>;
  name: Path<TFieldValues>;
}

export const AggregationTypeRadioToggleGroup = <
  TFieldValues extends FieldValues,
>({
  className,
  control,
  name,
  ...props
}: AggregationTypeRadioToggleGroupProps<TFieldValues>) => {
  const { t } = useAutoneTranslation<typeof APP_NAME>();

  return (
    <TailwindFormField
      control={control}
      name={name}
      render={({ field: { value, onChange } }) => (
        <CardToggleGroup.Root
          defaultValue={'product_location' satisfies AggregationType}
          value={value}
          onValueChange={(val) => (val ? onChange(val) : null)}
          {...props}
          className={cn('flex flex-row gap-1 h-fit border-none', className)}
        >
          <AggregationTypeRadioToggleGroupItem
            currValue={value}
            value={'product_location' satisfies AggregationType}
            optionTitle={t('template-granularity.product-level')}
            optionDescription={t('template-granularity.product-level-desc')}
          />
          <AggregationTypeRadioToggleGroupItem
            currValue={value}
            value={'sku_location' satisfies AggregationType}
            optionTitle={t('template-granularity.sku-level')}
            optionDescription={t('template-granularity.sku-level-desc')}
          />
        </CardToggleGroup.Root>
      )}
    />
  );
};

interface AggregationTypeRadioToggleGroupItemProps
  extends Omit<ToggleGroupItemProps, 'children'> {
  optionTitle: string;
  optionDescription: string;
  currValue: string;
}

const AggregationTypeRadioToggleGroupItem = ({
  className,
  optionTitle,
  optionDescription,
  value,
  currValue,
}: AggregationTypeRadioToggleGroupItemProps) => {
  return (
    <CardToggleGroup.Item className={className} value={value}>
      <CardToggleGroup.ItemHeader
        role={'radio'}
        aria-checked={currValue === value}
      >
        {optionTitle}
      </CardToggleGroup.ItemHeader>
      <CardToggleGroup.ItemDescription>
        {optionDescription}
      </CardToggleGroup.ItemDescription>
    </CardToggleGroup.Item>
  );
};
